/* eslint-disable no-alert */
import React, { useState, useCallback, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import NominalShareSelection from "./NominalShareSelection";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import {
  validateConsolidationAndSubdivision,
  deriveCounterpart,
} from "../../../utils/validateConsolidationAndSubdivision";
import { validateConsolidationSubdivisionAsync } from "../../../state/slices/shareConsolidationAndSubdivision";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import { useToast } from "hooks/useToast";

// Map camelCase processType → SCREAMING_SNAKE changeType
const processTypeToChangeType = {
  sharesConsolidation: "SHARES-CONSOLIDATION",
  sharesSubdivision:   "SHARES-SUBDIVISION",
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  n != null && n !== "" && !isNaN(Number(n))
    ? Number(n).toLocaleString()
    : "—";

const fmtDerived = (n) => {
  if (n == null || isNaN(n)) return "";
  if (Number.isInteger(n)) return String(n);
  return Number(n).toFixed(10).replace(/\.?0+$/, "");
};

// ─── ReorgSubRowForm ──────────────────────────────────────────────────────────
const ReorgSubRowForm = ({
  row,              // the row object from the table
  changeType,
  companyId,
  filteredOfficials,
  closeSubRow,
  onRefresh,        // callback to refresh parent data
}) => {
  const dispatch   = useDispatch();
  const { toast }  = useToast(5000);
  const share      = row.original;

  const shareClass =
    share.sharesCategory ||
    share.classOfShares ||
    share?.ProposalData?.sharesCategory ||
    share?.ProposalData?.classOfShares ||
    "";

  // Determine if we are editing an existing proposed entry
  const isEdit = !!share.newNumberOfShares && !!share.expectedShareValue;
  const existingTransactionId = share.trxId; // may be undefined

  const currentShares   = Number(share.previousNumberOfShares ?? share.sharesNumber) || 0;
  const currentParValue = Number(share.previousParValue ?? share.sharesValue) || 0;
  const nominalTotal    = currentShares * currentParValue;

  const initShares   = isEdit ? String(share.newNumberOfShares) : (currentShares > 0 ? String(currentShares) : "1");
  const initParValue = isEdit ? String(share.expectedShareValue) : (currentParValue > 0 ? fmtDerived(currentParValue) : "1");

  const [source,       setSource]       = useState(null);
  const [newShares,    setNewShares]    = useState(initShares);
  const [newParValue,  setNewParValue]  = useState(initParValue);
  const [errors,       setErrors]       = useState({});
  const [serverError,  setServerError]  = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createdTransactionsIds, setCreatedTransactionsIds } = useContext(
    UpdateCompanyDetailsContext
  );

  // Get nominal share record
  const nominalShares = useSelector(
    (state) => state.companyDashboardSlice?.company?.nominalShares || []
  );
  const nominalShare = nominalShares.find(
    (n) => n?.sharesCategory === shareClass
  ) || null;

  // Local validation
  const revalidate = useCallback(
    (sharesVal, parVal) => {
      const errs = validateConsolidationAndSubdivision({
        changeType,
        currentShares,
        currentParValue,
        nominalTotal,
        newShares:   sharesVal,
        newParValue: parVal,
      });
      setErrors(errs);
    },
    [changeType, currentShares, currentParValue, nominalTotal]
  );

  useEffect(() => {
    revalidate(initShares, initParValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSharesChange = useCallback(
    (e) => {
      const raw = e.target.value;
      const normalized =
        raw === ""
          ? ""
          : raw.includes(".")
          ? String(Math.floor(Number(raw)))
          : raw;

      const sharesNum = Number(normalized);
      const derivedPV =
        normalized !== "" && sharesNum > 0 && nominalTotal > 0
          ? fmtDerived(nominalTotal / sharesNum)
          : newParValue;

      setSource("newShares");
      setNewShares(normalized);
      setNewParValue(derivedPV);
      setServerError(null);
      revalidate(normalized, derivedPV);
    },
    [nominalTotal, newParValue, revalidate]
  );

  const handleParValueChange = useCallback(
    (e) => {
      const raw    = e.target.value;
      const pvNum  = Number(raw);
      const derivedShares =
        raw !== "" && pvNum > 0 && nominalTotal > 0
          ? fmtDerived(nominalTotal / pvNum)
          : newShares;

      setSource("newParValue");
      setNewParValue(raw);
      setNewShares(derivedShares);
      setServerError(null);
      revalidate(derivedShares, raw);
    },
    [nominalTotal, newShares, revalidate]
  );

  const handleSave = async () => {
    // Local validation
    const localErrs = validateConsolidationAndSubdivision({
      changeType,
      currentShares,
      currentParValue,
      nominalTotal,
      newShares,
      newParValue,
    });
    if (Object.keys(localErrs).length > 0) {
      setErrors(localErrs);
      return;
    }

    setIsValidating(true);
    setServerError(null);
    setErrors({});

    try {
      // Backend distribution validation
      await dispatch(
        validateConsolidationSubdivisionAsync({
          changeType,
          oldSharesNumber:  currentShares,
          newSharesNumber:  Number(newShares),
          newParValue:      Number(newParValue),
          sharesCategory:   shareClass,
          filteredOfficials,
          companyId,
        })
      ).unwrap();

      setIsValidating(false);
      setIsSubmitting(true);

      const newSharesNum    = Number(newShares);
      const newParVal       = Number(newParValue);
      const newNominalTotal = newSharesNum * newParVal;
      const txChangeType    = processTypeToChangeType[changeType];

      const ratio =
        currentShares > 0 && newSharesNum > 0
          ? newSharesNum / currentShares
          : 0;

      const allocations =
        Array.isArray(filteredOfficials) && shareClass && ratio > 0
          ? filteredOfficials
              .map((official) => {
                const allotments = official.sharesAllotted || [];
                const classEntries = allotments.filter(
                  (s) =>
                    s.classOfShares === shareClass &&
                    Number(s.allottedShares || 0) > 0
                );
                const totalOld = classEntries.reduce(
                  (sum, s) => sum + (Number(s.allottedShares || 0) || 0),
                  0
                );
                if (totalOld <= 0) return null;

                const newAllocated = Math.round(totalOld * ratio);

                const certificatesToCancel = classEntries
                  .map((s) => ({
                    certId: s._id,
                    certNo: s.numberOfAllotmentLetter,
                  }))
                  .filter((c) => c.certId);

                const name =
                  official?.names?.fullName ||
                  (
                    (official?.names?.firstName || "") +
                    " " +
                    (official?.names?.lastName || "")
                  ).trim() ||
                  official?.names?.companyName ||
                  official._id;

                return {
                  officialId:      official._id,
                  name,
                  currentShares:   totalOld,
                  allocatedShares: newAllocated,
                  sharesBalance:   newAllocated,
                  certificatesToCancel,
                  sharePrice:      newParVal,
                  type:            changeType,
                };
              })
              .filter(Boolean)
          : [];

      const shareCapitalChange = {
        changeType,
        classOfShares:          shareClass,
        nominalShareId:         nominalShare?._id || null,
        previousNumberOfShares: currentShares,
        previousParValue:       currentParValue,
        nominalValueTotal:      nominalTotal,
        newNumberOfShares:      newSharesNum,
        expectedShareValue:     newParVal,
        newNominalValueTotal:   newNominalTotal,
        offerPricePerShare:     newParVal,
        premiumPerShare:        0,
        sharesOnOffer:          newSharesNum,
        allocations,
      };

      let action;

      // If we have an existing transaction ID (editing), update it directly
      if (existingTransactionId) {
        const fetchAction = await dispatch(
          getAllInitiateTransactionChangeAsync({
            companyId,
            params: {
              transactionType: "MANUAL",
              transactionIds: [existingTransactionId],
            },
          })
        );
        const existingTx = fetchAction?.payload?.items?.[0];
        if (!existingTx) {
          throw new Error("Transaction not found.");
        }
        let updatedChanges = (existingTx.shareCapitalChanges || []).map((c) =>
          c.classOfShares === shareClass ? { ...c, ...shareCapitalChange } : c
        );
        if (!updatedChanges.some((c) => c.classOfShares === shareClass)) {
          updatedChanges.push(shareCapitalChange);
        }

        updatedChanges = updatedChanges.map((c) => ({
          ...c,
          changeType: c.changeType || changeType,
          classOfShares: c.classOfShares || shareClass,
          sharesOnOffer:
            c.sharesOnOffer ||
            c.newNumberOfShares ||
            c.previousNumberOfShares ||
            newSharesNum ||
            currentShares,
        }));
        action = await handleCompanyMaintenanceFormData(
          "existing-request",
          {
            companyId,
            documentId: existingTransactionId,
            formName: "initiateTransactionChange",
            changeType: txChangeType,
            processType: changeType,
            transactionType: "MANUAL",
            applicationType: "Share Capital",
            shareCapitalChanges: updatedChanges,
          },
          companyId
        );
      } else {
        // Check for an existing DRAFT transaction (create new or append)
        const fetchAction = await dispatch(
          getAllInitiateTransactionChangeAsync({
            companyId,
            params: {
              transactionType: "MANUAL",
              changeType:      txChangeType,
            },
          })
        );

        const existingTxns = fetchAction?.payload?.items || [];
        const draftTxn     = existingTxns.find(
          (tx) => tx.changeType === txChangeType && tx.status === "DRAFT"
        );

        if (draftTxn?._id) {
          let updatedChanges = (draftTxn.shareCapitalChanges || []).some(
            (c) => c.classOfShares === shareClass
          )
            ? (draftTxn.shareCapitalChanges || []).map((c) =>
                c.classOfShares === shareClass ? { ...c, ...shareCapitalChange } : c
              )
            : [...(draftTxn.shareCapitalChanges || []), shareCapitalChange];

          updatedChanges = updatedChanges.map((c) => ({
            ...c,
            changeType: c.changeType || changeType,
            classOfShares: c.classOfShares || shareClass,
            sharesOnOffer:
              c.sharesOnOffer ||
              c.newNumberOfShares ||
              c.previousNumberOfShares ||
              newSharesNum ||
              currentShares,
          }));

          action = await handleCompanyMaintenanceFormData(
            "existing-request",
            {
              companyId,
              documentId: draftTxn._id,
              formName: "initiateTransactionChange",
              changeType: txChangeType,
              processType: changeType,
              transactionType: "MANUAL",
              applicationType: "Share Capital",
              shareCapitalChanges: updatedChanges,
            },
            companyId
          );
        } else {
          action = await handleCompanyMaintenanceFormData(
            "new-request",
            {
              companyId,
              formName:            "initiateTransactionChange",
              changeType:          txChangeType,
              processType:         changeType,
              transactionType:     "MANUAL",
              applicationType:     "Share Capital",
              shareCapitalChanges: [
                {
                  ...shareCapitalChange,
                  sharesOnOffer:
                    shareCapitalChange.sharesOnOffer ||
                    shareCapitalChange.newNumberOfShares ||
                    shareCapitalChange.previousNumberOfShares ||
                    currentShares,
                },
              ],
            },
            companyId
          );
        }
      }

      const isSuccess =
        action?.type === "transactionChange/editInitiatedTransactionChangeAsync/fulfilled" ||
        action?.type === "transactionChange/createTransactionChangeAsync/fulfilled";

      if (!isSuccess) {
        throw new Error(
          action?.payload?.messageText || "Failed to save transaction."
        );
      }

      // Track transaction ID if new
      if (!existingTransactionId && action?.payload?.savedTransaction?._id) {
        setCreatedTransactionsIds((prev) => [...prev, action.payload.savedTransaction._id]);
      }

      toast(
        "success",
        `${changeType === "sharesConsolidation" ? "Consolidation" : "Subdivision"} saved successfully.`
      );

      // Refresh parent data
      onRefresh?.();

      closeSubRow?.();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message ||
            "Server validation failed. Please check your inputs and try again.";
      setServerError(msg);
    } finally {
      setIsValidating(false);
      setIsSubmitting(false);
    }
  };

  const isConsolidation = changeType === "sharesConsolidation";
  const nsNum  = Number(newShares);
  const npvNum = Number(newParValue);
  const newTotal =
    newShares !== "" && newParValue !== "" &&
    !isNaN(nsNum) && !isNaN(npvNum) && nsNum > 0 && npvNum > 0
      ? nsNum * npvNum
      : null;
  const totalMatches     = newTotal !== null && Math.abs(newTotal - nominalTotal) < 0.000001;
  const sharesIsFraction = newShares !== "" && !isNaN(nsNum) && nsNum > 0 && !Number.isInteger(nsNum);
  const hasErrors        = Object.keys(errors).length > 0;
  const isButtonDisabled = isValidating || isSubmitting || hasErrors;

  const inputStyle = (field, isDerived, hasFraction) => ({
    width:        "100%",
    boxSizing:    "border-box",
    border:       errors[field]
      ? "1.5px solid #dc2626"
      : hasFraction
      ? "1.5px solid #f59e0b"
      : "1px solid #d1c9e0",
    borderRadius: 8,
    padding:      "10px 14px",
    fontSize:     14,
    color:        "#111827",
    outline:      "none",
    background:   isDerived && (newShares || newParValue) ? "#f9f7ff" : "#fff",
    transition:   "border-color 0.15s, background 0.15s",
  });

  return (
    <div
      style={{
        fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif",
        padding: "24px 28px 0",
        background: "#fff",
        borderTop: "1px solid #e8e3f0",
        borderBottom: "1px solid #e8e3f0",
      }}
    >
      {serverError && (
        <div
          style={{
            marginBottom: 18,
            padding: "12px 16px",
            borderRadius: 8,
            backgroundColor: "#fee2e2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontSize: 13,
          }}
        >
          {serverError}
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 700,
            color: "#2d1a45",
            marginBottom: 6,
            letterSpacing: "0.01em",
          }}
        >
          Class of Shares
        </label>
        <div
          style={{
            background: "#f5f3f8",
            border: "1px solid #e2dded",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 14,
            color: "#6b7280",
          }}
        >
          {shareClass || "Ordinary"}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 22,
          fontSize: 13.5,
          color: "#374151",
        }}
      >
        <span>
          Current:{" "}
          <strong style={{ color: "#2d1a45" }}>{fmt(currentShares)} shares</strong>
          {" @ "}
          <strong style={{ color: "#2d1a45" }}>KES {fmt(currentParValue)}</strong>
          {" each"}
        </span>
        <span style={{ color: "#c4b8d8" }}>|</span>
        <span>
          Nominal total:{" "}
          <strong style={{ color: "#2d1a45" }}>KES {fmt(nominalTotal)}</strong>
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginBottom: 16,
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 700,
              color: "#2d1a45",
              marginBottom: 6,
            }}
          >
            New Number of Shares
            {currentShares > 0 && (
              <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 4 }}>
                — must be {isConsolidation ? "less" : "more"} than {fmt(currentShares)}
              </span>
            )}
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={newShares}
            onChange={handleSharesChange}
            onFocus={() => setSource("newShares")}
            placeholder="Enter number of shares"
            style={inputStyle("newShares", source === "newParValue", sharesIsFraction)}
          />
          {errors.newShares ? (
            <p style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
              {errors.newShares}
            </p>
          ) : source === "newParValue" && newShares ? (
            <p style={{ color: "#6b7280", fontSize: 12, marginTop: 4, fontStyle: "italic" }}>
              Auto-calculated from par value
            </p>
          ) : null}
        </div>

        <div>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 700,
              color: "#2d1a45",
              marginBottom: 6,
            }}
          >
            New Par Value per Share (KES)
            {currentParValue > 0 && (
              <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 4 }}>
                — must be {isConsolidation ? "higher" : "lower"} than {fmt(currentParValue)}
              </span>
            )}
          </label>
          <input
            type="number"
            min="0.0001"
            step="any"
            value={newParValue}
            onChange={handleParValueChange}
            onFocus={() => setSource("newParValue")}
            placeholder="Enter value per share"
            style={inputStyle("newParValue", source === "newShares", false)}
          />
          {errors.newParValue ? (
            <p style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
              {errors.newParValue}
            </p>
          ) : source === "newShares" && newParValue ? (
            <p style={{ color: "#6b7280", fontSize: 12, marginTop: 4, fontStyle: "italic" }}>
              Auto-calculated from number of shares
            </p>
          ) : null}
        </div>
      </div>

      {newTotal !== null && (
        <div
          style={{
            marginBottom: 20,
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: 13,
            background: totalMatches ? "#f0fdf4" : "#fef9c3",
            border:     totalMatches ? "1px solid #86efac" : "1px solid #fde047",
            color:      totalMatches ? "#15803d" : "#854d0e",
          }}
        >
          {totalMatches ? (
            <>
              ✓ New total:{" "}
              <strong>
                {fmt(nsNum)} shares x KES {fmt(npvNum)} = KES {fmt(newTotal)}
              </strong>{" "}
              — matches nominal total.
            </>
          ) : (
            <>
              New total <strong>KES {fmt(newTotal)}</strong> does not match
              nominal total of <strong>KES {fmt(nominalTotal)}</strong>.
            </>
          )}
        </div>
      )}

      <div className="flex justify-end mt-2 -mx-7 px-6 pb-4 pt-4 bg-gray-50 rounded-b-lg border-t border-gray-100">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={closeSubRow}
            disabled={isValidating || isSubmitting}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isButtonDisabled}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sflPrimary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isValidating ? (
              <>
                <SpinnerIcon className="w-4 h-4 mr-2 -ml-1 animate-spin" />
                Validating...
              </>
            ) : isSubmitting ? (
              <>
                <SpinnerIcon className="w-4 h-4 mr-2 -ml-1 animate-spin" />
                Submitting...
              </>
            ) : (
              "Validate & Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main TableSubRow ─────────────────────────────────────────────────────────
const TableSubRow = (props) => {
  const isReorgChangeType =
    props.changeType === "sharesConsolidation" ||
    props.changeType === "sharesSubdivision";

  if (isReorgChangeType) {
    return (
      <tr className="w-full">
        <td colSpan={props.visibleColumns.length} style={{ padding: 0 }}>
          <ReorgSubRowForm
            row={props.row}
            filteredOfficials={props.filteredOfficials}
            companyId={props.companyId}
            changeType={props.changeType}
            closeSubRow={props.closeSubRow}
            onRefresh={props.onRefresh}
          />
         </td>
       </tr>
    );
  }

  return (
    <tr className="w-full">
      <td colSpan={props.visibleColumns.length}>
        <NominalShareSelection
          changeType={props.changeType}
          isInManualUpdate={props.isInManualUpdate}
          approvedShareAuthorizations={props.approvedShareAuthorizations}
          mode={props.mode}
          takenShares={props.takenShares}
          closeSubRow={props.closeSubRow}
          sharesCategory={props.row.original.sharesCategory}
          addDifferentCategory={true}
          nominalShares={props.nominalShares}
          isProposed={props.isProposed}
        />
      </td>
    </tr>
  );
};

export default TableSubRow;
